import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { EMPTY, Observable, catchError, finalize, tap } from 'rxjs';
import { CheckMfa, LoadLoginConfiguration, LogIn, MfaTokenExpiredError, UpdateStep } from '@pages/login/store/actions/login.actions';
import { LoginService } from '@pages/login/services/login.service';
import { LoginStep } from '@pages/login/models/login-step.enum';
import { LoginConfigurationResponse } from '@pages/login/models/login-configuration/login-configuration-response.type';
import { LoginAttempt } from '@core/authorization/models/login-attempt.interface';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { AuthorizationData } from '@core/authorization/models/authorization-data.interface';

interface LoginStateModel {
  loginConfiguration?: LoginConfigurationResponse;
  loginAttempt?: LoginAttempt;
  authorizationData?: AuthorizationData;
  loginConfigurationLoading: boolean;
  logInLoading: boolean;
  checkMfaLoading: boolean;
  currentStep: LoginStep;
  errorMessageTranslationKey: string;
}

@State<LoginStateModel>({
  name: 'login',
  defaults: {
    loginConfigurationLoading: false,
    logInLoading: false,
    checkMfaLoading: false,
    currentStep: LoginStep.Configuration,
    errorMessageTranslationKey: '',
  },
})
@Injectable()
export class LoginState {
  private readonly loginService = inject(LoginService);

  @Selector()
  static loginConfiguration(state: LoginStateModel): LoginConfigurationResponse | undefined {
    return state.loginConfiguration;
  }

  @Selector()
  static loginAttempt(state: LoginStateModel): LoginAttempt | undefined {
    return state.loginAttempt;
  }

  @Selector()
  static authorizationData(state: LoginStateModel): AuthorizationData | undefined {
    return state.authorizationData;
  }

  @Selector()
  static currentStep(state: LoginStateModel): LoginStep {
    return state.currentStep;
  }

  @Selector()
  static errorMessageTranslationKey(state: LoginStateModel): string {
    return state.errorMessageTranslationKey;
  }

  @Selector()
  static samlLoginUrl(state: LoginStateModel): string | undefined {
    return state.loginConfiguration?.samlLoginUrl ?? undefined;
  }

  @Selector()
  static logInLoading(state: LoginStateModel): boolean {
    return state.logInLoading;
  }

  @Selector()
  static checkMfaLoading(state: LoginStateModel): boolean {
    return state.checkMfaLoading;
  }

  @Selector()
  static loginConfigurationLoading(state: LoginStateModel): boolean {
    return state.loginConfigurationLoading;
  }

  @Action(LoadLoginConfiguration)
  loadLoginConfiguration(
    context: StateContext<LoginStateModel>,
    { loginConfiguration }: LoadLoginConfiguration
  ): Observable<LoginConfigurationResponse> {
    context.patchState({ loginConfigurationLoading: true, errorMessageTranslationKey: '' });

    return this.loginService.getLoginInfo(loginConfiguration).pipe(
      finalize(() => context.patchState({ loginConfigurationLoading: false })),
      tap(loginConfigurationResponse => context.patchState({ loginConfiguration: loginConfigurationResponse }))
    );
  }

  @Action(LogIn)
  logIn(context: StateContext<LoginStateModel>, { credentials }: LogIn): Observable<LoginAttempt> {
    context.patchState({ logInLoading: true, loginAttempt: undefined });

    return this.loginService.logIn(credentials).pipe(
      finalize(() => context.patchState({ logInLoading: false })),
      tap(loginAttempt => context.patchState({ loginAttempt })),
      catchError(({ status, error }: HttpErrorResponse) => {
        if (status === HttpStatusCode.UnavailableForLegalReasons) {
          context.patchState({
            errorMessageTranslationKey: 'login.messages.ip-not-allowed',
            currentStep: LoginStep.Configuration,
          });
        }

        if (status === HttpStatusCode.BadRequest) {
          context.patchState({
            errorMessageTranslationKey: error === 'InvalidCredentials' ? 'login.messages.invalid-login-error' : '',
            currentStep: LoginStep.Configuration,
          });
        }

        return EMPTY;
      })
    );
  }

  @Action(CheckMfa)
  checkMfa(context: StateContext<LoginStateModel>, { mfaToken }: CheckMfa): Observable<AuthorizationData> {
    context.patchState({ checkMfaLoading: true, authorizationData: undefined });

    return this.loginService.checkMfa(mfaToken).pipe(
      finalize(() => context.patchState({ checkMfaLoading: false })),
      tap(authorizationData => context.patchState({ authorizationData })),
      catchError(({ status }: HttpErrorResponse) => {
        if (status === HttpStatusCode.BadRequest) {
          context.patchState({
            errorMessageTranslationKey: 'login.messages.invalid-login-error',
            currentStep: LoginStep.Configuration,
          });
        }

        if (status === HttpStatusCode.Unauthorized) {
          context.patchState({ errorMessageTranslationKey: '', currentStep: LoginStep.Configuration });
          context.dispatch(new MfaTokenExpiredError());
        }

        return EMPTY;
      })
    );
  }

  @Action(UpdateStep)
  updateStep(context: StateContext<LoginStateModel>, { loginStep }: UpdateStep): void {
    context.patchState({ currentStep: loginStep });
  }
}
